import { Outlet, useMatch } from 'react-router';

import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';

import { AnalysisSectionLayout, SectionBlocks, type SectionBlock } from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { toNumberedListItems, useCorePersonas, type RawTitleDescription } from './lib';
import { useAudienceDrafts } from './lib/useAudienceDrafts';
import {
  AdjacentAudiences,
  CoreSegments,
  SubculturesAndCommunities,
  SuggestedAudiencesNotice,
} from './components';
import { useTranslation } from 'react-i18next';

interface AudienceData {
  subCultures?: RawTitleDescription[];
  adjacentAudiences?: RawTitleDescription[];
}

const AudienceProfilesSection = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const title = t('analysisResult.audienceProfiles.audienceProfiles');
  const analysis = useAnalysisFromContext(analysisProp);
  const { audience } = analysis.structuredData || {};
  const audienceData = audience as AudienceData | undefined;

  // TanStack Query dedupes this fetch with the one inside CoreSegments. While it loads, Core
  // Segments holds its place so the page does not flash the empty state first.
  const { hasPersonas, isLoading: personasLoading } = useCorePersonas(
    analysis.personaIds ?? undefined,
  );
  const { cards, isBuilding, hasFailures, retry, isRetrying } = useAudienceDrafts(analysis, {
    exporting,
  });

  const blocks: SectionBlock[] = [
    {
      key: 'core-segments',
      hasData: personasLoading || hasPersonas || cards.length > 0,
      node: (
        <StaggerItem index={0} variant="swipeUp">
          <div className="flex flex-col gap-5">
            <CoreSegments personaIds={analysis.personaIds ?? undefined} suggested={cards} />
            <SuggestedAudiencesNotice
              isBuilding={isBuilding}
              hasFailures={hasFailures}
              isRetrying={isRetrying}
              onRetry={retry}
            />
          </div>
        </StaggerItem>
      ),
    },
    {
      key: 'subcultures',
      hasData: toNumberedListItems(audienceData?.subCultures).length > 0,
      node: (
        <StaggerItem index={1} variant="swipeUp">
          <SubculturesAndCommunities subcultures={audienceData?.subCultures} />
        </StaggerItem>
      ),
    },
    {
      key: 'adjacent',
      hasData: toNumberedListItems(audienceData?.adjacentAudiences).length > 0,
      node: (
        <StaggerItem index={2} variant="swipeUp">
          <AdjacentAudiences adjacentAudiences={audienceData?.adjacentAudiences} />
        </StaggerItem>
      ),
    },
  ];

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_AUDIENCE_PROFILES}
      title={title}
      subtitle={t('analysisResult.audienceProfiles.coreSegmentsCommunitiesAdjacencies', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      <SectionBlocks category={title} blocks={blocks} />
    </AnalysisSectionLayout>
  );
};

/**
 * The persona-detail route (`.../audience-profiles/:personaId`) renders its own page through
 * the outlet. It is checked out here rather than inside the section so that the section's
 * persona and draft fetches never start on a route that does not show them.
 *
 * Personas come from `personaIds`, set when the analysis was created and never touched by
 * extraction, so an empty audience slice hides only the subculture and adjacency blocks.
 */
export const AudienceProfiles = (props: AnalysisSectionProps = {}) => {
  const isPersonaDetail = useMatch('/iq/:analysisId/audience-profiles/:personaId');

  if (isPersonaDetail) {
    return <Outlet />;
  }

  return <AudienceProfilesSection {...props} />;
};
